// TUT-230 — a definição de uma etapa do processo.
//
// A cliente descreveu um PADRÃO, não uma tela (10/09, 03:24 e 04:09): cada
// etapa tem o seu resumo em cima e o seu quadro embaixo, com os status DAQUELA
// etapa. Quando perguntamos se era filtro, ela corrigiu (27:16): "não é um
// filtro, porque a coluna não muda". Filtro esconde cartão e mantém coluna;
// aqui a coluna é outra — e é isso que separa uma etapa da outra.
//
// Este módulo é o VALOR ÚNICO que as duas metades leem. Se o resumo contasse
// por um caminho e o quadro agrupasse por outro, um contador acabaria
// discordando da coluna embaixo dele — e um número que mente é pior do que
// número nenhum.

/** O item, reduzido ao que decide a coluna. Peça, laudo ou perícia. */
import type { ChaveDeFoco } from "./foco";

export interface ItemDaEtapa {
  id: string;
  /** Status bruto do domínio (`LegalBriefStatusName`, `ExpertiseStatusKey`…). */
  status: string;
  /** Quem pegou o trabalho. `null` = ninguém pegou. */
  responsavelId: string | null;
  /** Prazo em ISO 8601, quando houver. */
  prazoEm: string | null;
}

export interface ColunaDaEtapa {
  chave: string;
  titulo: string;
  /** Se o item pertence a esta coluna. */
  aceita: (item: ItemDaEtapa) => boolean;
  /**
   * Para onde o trabalho CONTINUA depois desta coluna (TUT-249 fase 2).
   *
   * São as setas que ela desenhou no quadro do Canva, saindo das colunas da
   * Elaboração e entrando nos contadores do Agendamento: *"para ela, as duas
   * telas são um fluxo só"* — o que a Elaboração termina vira pendência do
   * Agendamento (decisão 17 da fase 2, respondida pelo desenho).
   *
   * Isto é do DOMÍNIO, e não da tela, porque quem sabe que "Finalizados de
   * laudo" e "Aguardando protocolo" são a MESMA linha é o vocabulário das
   * etapas, não o componente que as desenha.
   */
  continuaEm?: ContinuacaoDaEtapa;
  /**
   * TUT-530 — a coluna sem "Ver lista": ela não é uma das pendências do topo
   * (que abrem lista), e sim o estado de um envio ao advogado que espera o
   * advogado ("Aguardando cliente").
   */
  semLista?: true;
}

/**
 * O elo entre uma coluna e o contador que a recebe na tela seguinte.
 *
 * `mesmasLinhas` não é detalhe: existem dois tipos de seta no desenho dela, e
 * confundi-los faria a tela mentir.
 *
 * - **Mesma linha** — "Finalizados" de peça é literalmente `AWAITING_DELIVERY`,
 *   que é o que o Agendamento conta como "Pendente envio"; o laudo em
 *   `AWAITING_FILING` normaliza para o mesmo "Finalizados" e, ainda sem envio,
 *   também é "Pendente envio" (TUT-530, revisão), mas o "Finalizados" do laudo
 *   guarda também o já protocolado: ali o elo NÃO é de mesma linha.
 * - **Destrave** — "Finalizados" de quesitos aponta para "Aguardando perícia",
 *   e ali NÃO são as mesmas linhas: terminar os quesitos libera a perícia,
 *   que é outro registro. Prometer contagem igual nesse caso seria inventar
 *   uma relação que o dado não tem.
 */
export interface ContinuacaoDaEtapa {
  /**
   * A chave do foco no Agendamento — lista FECHADA, importada de `lib/foco.ts`.
   *
   * Tipar como `string` deixaria um erro de digitação chegar à URL e abrir o
   * pop-up vazio, em silêncio. `foco.ts` não importa este módulo, então não há
   * ciclo.
   */
  foco: ChaveDeFoco;
  /** O que a pessoa lê no elo. */
  rotulo: string;
  /** Verdadeiro quando o destino conta AS MESMAS linhas desta coluna. */
  mesmasLinhas: boolean;
}

export type ChaveDeEtapa = "elaboracao" | "agendamento";

/**
 * A chave de um QUADRO — e ela não é a chave de uma ETAPA.
 *
 * `ChaveDeEtapa` indexa `etapas`, o catálogo das duas telas de TRABALHO, e
 * alargá-la obrigaria aquele registro a ganhar entradas que não existem. A
 * primeira versão desta mudança fez exatamente isso, e o typecheck pegou: as
 * Propostas (TUT-250) e o Financeiro (TUT-251) desenham quadros, mas não são
 * etapas do processo.
 */
export type ChaveDeQuadro =
  | ChaveDeEtapa
  | "propostas"
  | "financeiro-a-receber"
  | "financeiro-a-pagar";

export interface DefinicaoDeEtapa {
  chave: ChaveDeQuadro;
  titulo: string;
  /**
   * A frase sob o título, quando o quadro tem uma. O das Propostas não tem:
   * a cliente pediu para tirar (TUT-481, item 7d) e o cabeçalho ficou só com
   * o título.
   */
  subtitulo?: string;
  colunas: ColunaDaEtapa[];
}

/**
 * "Vencido" NÃO é coluna — é prazo estourado.
 *
 * Ela citou vencido junto das outras colunas (25:41), e a leitura literal
 * viraria uma sexta coluna. Mas um cartão pode ser `DRAFTING` **e** vencido ao
 * mesmo tempo: uma coluna obrigaria a escolher entre as duas verdades e
 * esconderia EM QUE ETAPA o atraso está — que é o que ela quer enxergar. Os
 * quadros que ela mostrou na reunião já resolvem melhor, com selo no cabeçalho
 * da coluna ("21 vencidos", "4 vencidos").
 */
export function estaVencido(item: ItemDaEtapa, agora: Date): boolean {
  const prazo = dataDoPrazo(item);
  if (!prazo) return false;
  // TUT-321 — vencido é o que venceu num DIA anterior. No próprio dia o prazo
  // ainda "vence" (pedido do dono, 26/09): antes, a peça de hoje às 9h vista
  // ao meio-dia caía em "Hoje" E em "Atrasados", e o cartão dizia "Venceu".
  return prazo.getTime() < inicioDoDia(agora).getTime();
}

/**
 * TUT-472 (Q067) — o que já terminou não vence. "Finalizados" aceita a peça
 * ENTREGUE (e o laudo protocolado, normalizado para `delivered` na borda da
 * tela): o prazo dela é história, e contá-la em "Atrasados" com "Venceu" em
 * vermelho dizia que um trabalho entregue estava em atraso. A mesma leitura
 * que o Financeiro faz da despesa paga (`paraItemDeDespesa`). Vocabulário em
 * minúsculas, o de `ItemDaEtapa.status`.
 */
export const ENCERRADOS: ReadonlySet<string> = new Set(["delivered", "filed", "not_delivered", "cancelled"]);

function dataDoPrazo(item: ItemDaEtapa): Date | null {
  if (!item.prazoEm || ENCERRADOS.has(item.status)) return null;
  const prazo = new Date(item.prazoEm);
  return Number.isNaN(prazo.getTime()) ? null : prazo;
}

function inicioDoDia(data: Date): Date {
  const inicio = new Date(data.getTime());
  inicio.setHours(0, 0, 0, 0);
  return inicio;
}

/**
 * TUT-321 — o texto do prazo no cartão, numa regra só para todos os quadros:
 * "Venceu 25/09" a partir do dia seguinte, "Vence hoje" no dia, "Vence 30/09"
 * antes. Os dois quadros montavam isso à mão, cada um com a sua conta.
 */
export function rotuloDoPrazo(item: ItemDaEtapa, agora: Date): string | null {
  return dataDoPrazo(item) ? rotuloDoVencimento(item.prazoEm, agora) : null;
}

/** A mesma regra para quem tem só a data (Minha carga, pop-up da Minha mesa). */
export function rotuloDoVencimento(iso: string | null, agora: Date): string | null {
  const prazo = iso ? new Date(iso) : null;
  if (!prazo || Number.isNaN(prazo.getTime())) return null;
  if (prazo.getTime() < inicioDoDia(agora).getTime()) {
    return `Venceu ${DIA_E_MES_DO_PRAZO.format(prazo)}`;
  }
  if (mesmoDia(prazo, agora)) return "Vence hoje";
  return `Vence ${DIA_E_MES_DO_PRAZO.format(prazo)}`;
}

const DIA_E_MES_DO_PRAZO = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
});

export interface ColunaDistribuida {
  chave: string;
  titulo: string;
  itens: ItemDaEtapa[];
  /** O selo do cabeçalho: quantos desta coluna estão atrasados. */
  vencidos: number;
  /** TUT-249 fase 2 — o elo para a tela seguinte, quando a coluna tem um. */
  continuaEm?: ContinuacaoDaEtapa;
  /** TUT-530 — a coluna sem "Ver lista" (ver `ColunaDaEtapa.semLista`). */
  semLista?: true;
}

/**
 * Distribui os itens nas colunas da etapa, na ordem da definição.
 *
 * Um item cai na PRIMEIRA coluna que o aceita — assim duas regras que se
 * sobrepõem por engano não duplicam o cartão em dois lugares. Item que nenhuma
 * coluna aceita fica de fora do quadro de propósito: em Elaboração é o caso de
 * `NOT_DELIVERED` e `CANCELLED`, que ela não citou e que encheriam a tela que
 * precisa parecer mais simples.
 */
export function distribuir(
  definicao: DefinicaoDeEtapa,
  itens: ItemDaEtapa[],
  agora: Date = new Date(),
): ColunaDistribuida[] {
  const colunas: ColunaDistribuida[] = definicao.colunas.map((coluna) => ({
    chave: coluna.chave,
    titulo: coluna.titulo,
    itens: [],
    vencidos: 0,
    ...(coluna.continuaEm ? { continuaEm: coluna.continuaEm } : {}),
    ...(coluna.semLista ? { semLista: true as const } : {}),
  }));

  for (const item of itens) {
    const indice = definicao.colunas.findIndex((coluna) => coluna.aceita(item));
    if (indice < 0) continue;
    const alvo = colunas[indice];
    if (!alvo) continue;
    alvo.itens.push(item);
    if (estaVencido(item, agora)) alvo.vencidos += 1;
  }

  return colunas;
}

/**
 * Contagem por coluna, derivada de `distribuir`.
 *
 * NÃO é o resumo do topo da tela: o rascunho dela mostra ali outro recorte —
 * por pessoa e por prazo (Atribuídos a mim, Atrasados, Hoje, Semana). Esta
 * função existe para as guardas provarem que uma contagem por coluna nunca
 * discorda do quadro; nenhuma tela a desenha.
 */
export interface ContadorDaEtapa {
  chave: string;
  titulo: string;
  total: number;
  vencidos: number;
}

export function resumir(colunas: ColunaDistribuida[]): ContadorDaEtapa[] {
  return colunas.map((coluna) => ({
    chave: coluna.chave,
    titulo: coluna.titulo,
    total: coluna.itens.length,
    vencidos: coluna.vencidos,
  }));
}

// ---------------------------------------------------------------------------
// As instâncias que a cliente descreveu. As outras três etapas (Cadastros,
// Financeiro e Consultoria) NÃO nascem aqui: ela não descreveu os status de
// nenhuma delas, e chutar produziria exatamente a tela com cara de sistema de
// que ela reclamou. Entram depois da conversa em que ela as descrever.
// ---------------------------------------------------------------------------

const semDono = (item: ItemDaEtapa) =>
  item.responsavelId === null || item.responsavelId === "";

/**
 * Elaboração — peças E laudos na mesma fila.
 *
 * A distinção que ela mais insistiu não é um status: "ninguém pegou" ×
 * "você pegou ele" (28:46) é a presença de responsável dentro do MESMO
 * `DRAFTING`. Por isso a coluna nasce do par (status, dono), e o dono no
 * cartão é pré-requisito desta tela, não enfeite dela.
 */
// O MIOLO, comum às três atividades. Sai do rascunho que ela mandou em 10/09,
// não da transcrição da fala: lá ela escreveu as colunas, e escrito ganha de
// falado. A versão anterior desta constante tinha uma coluna "Envio" que o
// rascunho não tem, e chamava a primeira de "A fazer" em vez de "Aguardando
// elaboração".
/**
 * O miolo comum às três atividades, com o elo de saída de CADA uma.
 *
 * Virou função na TUT-249 fase 2 por um motivo só: as três compartilham as
 * quatro colunas, mas **a seta que sai de "Finalizados" é diferente em cada
 * uma** — o quesito destrava a perícia, a peça e o laudo vão para envio. Manter uma constante compartilhada obrigaria a remendar a
 * última coluna fora dela, e o próximo a ler acharia que as três são iguais.
 */
function miolo(continuaEm: ContinuacaoDaEtapa): ColunaDaEtapa[] {
  return [
    {
      chave: "aguardando-elaboracao",
      titulo: "Aguardando elaboração",
      aceita: (item) => item.status === "drafting" && semDono(item),
    },
    {
      chave: "em-andamento",
      titulo: "Em andamento",
      aceita: (item) => item.status === "drafting" && !semDono(item),
    },
    {
      chave: "aguardando-revisao",
      titulo: "Aguardando revisão",
      aceita: (item) => item.status === "in_review",
    },
    {
      chave: "finalizados",
      titulo: "Finalizados",
      aceita: (item) =>
        item.status === "awaiting_delivery" || item.status === "delivered",
      continuaEm,
    },
  ];
}

/**
 * Para onde vai o que cada quadro termina — as setas do Canva, uma a uma.
 *
 * **Quesitos → Aguardando perícia** é a única que ela desenhou por escrito, e é
 * um DESTRAVE: terminar os quesitos libera a perícia, que é outro registro.
 * Por isso `mesmasLinhas: false` — os dois números não têm por que bater.
 *
 * **Impugnação → Pendente envio** e **Laudos → Pendente envio** não foram
 * adivinhadas: são a MESMA linha, e dá para provar. "Finalizados" aceita
 * `awaiting_delivery`, que é exatamente o status que `foco.ts` conta em
 * "Pendente envio" (o comentário de lá já dizia isso); e o laudo em
 * `AWAITING_FILING`, ainda sem envio, é normalizado para `awaiting_delivery` na
 * borda da tela, caindo nesta mesma coluna — e é "Pendente envio" no
 * Agendamento também (TUT-530, revisão de 25/10: a M&H age; "Aguardando
 * protocolo" é só o que já foi enviado e espera o advogado). As duas setas vão
 * ao MESMO contador, e é o Canva que as junta ali.
 */
const CONTINUACOES = {
  quesitos: {
    foco: "pendente-pericia",
    rotulo: "Destrava a perícia",
    mesmasLinhas: false,
  },
  laudos: {
    foco: "pendente-envio",
    rotulo: "Laudo segue para envio",
    // "Finalizados" do laudo inclui o já protocolado (FILED), que não está em
    // "Pendente envio": o destino conta só os que ainda não foram enviados
    // (TUT-542, TC+3).
    mesmasLinhas: false,
  },
  impugnacao: {
    foco: "pendente-envio",
    rotulo: "Segue para envio",
    mesmasLinhas: true,
  },
} as const satisfies Record<AtividadeDaElaboracao, ContinuacaoDaEtapa>;

/**
 * A Elaboração tem TRÊS quadros, um por atividade — e é isto que ela quis
 * dizer com "não é um filtro, porque a coluna não muda" (27:16).
 *
 * As três compartilham o miolo e **cada uma acrescenta a espera que é sua**:
 * o laudo espera a perícia acontecer; a impugnação espera o laudo do perito.
 * A de quesitos não espera nada — por isso começa direto no miolo.
 */
export type AtividadeDaElaboracao = "quesitos" | "laudos" | "impugnacao";

export const quadrosDaElaboracao: Record<
  AtividadeDaElaboracao,
  DefinicaoDeEtapa
> = {
  quesitos: {
    chave: "elaboracao",
    titulo: "Quesitos",
    subtitulo: "Os quesitos que a equipe tem para elaborar.",
    colunas: miolo(CONTINUACOES.quesitos),
  },
  laudos: {
    chave: "elaboracao",
    titulo: "Laudos",
    subtitulo: "Os laudos, da perícia realizada até a entrega.",
    colunas: [
      {
        chave: "pericias-realizadas",
        titulo: "Perícias realizadas",
        // TUT-527 (D39) — a encerrada aguardando relatório também: é a mesma
        // perícia de "Aguardando relatório" no Agendamento, vista daqui.
        aceita: (item) => item.status === "completed" || item.status === "pending_report",
      },
      ...miolo(CONTINUACOES.laudos),
    ],
  },
  impugnacao: {
    chave: "elaboracao",
    titulo: "Impugnação",
    subtitulo: "As impugnações, da espera pelo laudo do perito até a entrega.",
    colunas: [
      {
        chave: "aguardando-laudo-perito",
        titulo: "Aguardando laudo perito",
        aceita: (item) => item.status === "awaiting_expert_report",
      },
      ...miolo(CONTINUACOES.impugnacao),
    ],
  },
};

/**
 * O quadro padrão ao abrir a tela (abas "Todos" e "Quesitos").
 *
 * Sobre as colunas de cabeça dos outros dois quadros — o revisor corrigiu a
 * primeira versão deste comentário, que era parcialmente falsa:
 * - "Perícias realizadas": `completed` EXISTE em `ExpertiseStatusKey`, e desde
 *   a TUT-242 a fila de laudos entre processos também existe
 *   (`api.expertReportsAcrossProcesses`, consumida por `ElaboracaoPage.tsx`).
 *   Perícia e laudo COEXISTEM ali como uma UNIÃO de duas origens — o
 *   contorno de `ItemDaEtapa` carregar UM status foi resolvido na BORDA da
 *   tela (`paraItemDeLaudo`), que normaliza o vocabulário nativo de cada
 *   origem para o que esta coluna espera, e não aqui.
 * - "Aguardando laudo perito": `awaiting_expert_report` — TUT-291a deu status
 *   real a este estado (`LegalBriefStatusKey`/`LegalBriefStatusName`), a
 *   impugnação que nasce sozinha quando o laudo do assistente sai de
 *   revisão. A tela (`ElaboracaoPage.tsx`) só pede este status À API quando a
 *   aba impugnação está ativa — nas outras a coluna de cabeça segue vazia,
 *   porque nenhuma OUTRA atividade passa por este estado.
 */
export const etapaElaboracao: DefinicaoDeEtapa =
  quadrosDaElaboracao.quesitos;

/** As atividades que a tela consegue separar hoje. */
export type AtividadeDisponivel = "todos" | "quesitos" | "laudos" | "impugnacao";

/**
 * O recorte do topo, como ela desenhou: por PESSOA e por PRAZO — não pelas
 * colunas do quadro. Foi o rascunho de 10/09 que corrigiu isto; a primeira
 * versão derivava os contadores das colunas, e ela quer outra pergunta
 * respondida ali em cima.
 */
//
// TUT-313 — "Atribuídos a mim" SAIU daqui. Ele era um quarto membro do grupo
// exclusivo e, ao mesmo tempo, um ESCOPO (muda a consulta, e os outros
// contadores recontam dentro dele): clicar em "Atrasados" com "meus" ligado
// trocava um pelo outro, e o escopo sumia — o 24 → 0 → 24 que o dono viu em
// 26/09. Agora ele é a chave própria `meus` da busca, e o recorte é só por
// PRAZO, exclusivo dentro do escopo.
export type RecorteDaElaboracao = "atrasados" | "hoje" | "semana";

/**
 * As chaves do recorte como LISTA, e o reconhecedor delas (TUT-249).
 *
 * A lista fechada precisa existir em valor, e não só em tipo, porque quem a
 * confere é o `validateSearch` da rota: a URL é entrada do usuário, e
 * `?recorte=inventado` tem de virar ausência em vez de um recorte que a tela
 * não sabe aplicar. Tipo sozinho some na compilação e não confere nada.
 */
export const RECORTES_DA_ELABORACAO = [
  "atrasados",
  "hoje",
  "semana",
] as const satisfies readonly RecorteDaElaboracao[];

export function isRecorteDaElaboracao(
  valor: string,
): valor is RecorteDaElaboracao {
  return (RECORTES_DA_ELABORACAO as readonly string[]).includes(valor);
}

/**
 * O que o SELETOR do topo oferece — as três atividades mais "Todos".
 *
 * Não é o mesmo que `AtividadeDaElaboracao`, e a diferença tem razão de ser:
 * aquele tipo é o das atividades que têm QUADRO PRÓPRIO (cada uma com as suas
 * colunas), e "Todos" não tem — ele usa o miolo comum às três. Fundir os dois
 * faria `quadrosDaElaboracao` prometer um quadro de "Todos" que não existe.
 *
 * A lista precisa existir em VALOR, e não só em tipo, porque quem a confere é
 * o `validateSearch` da rota: a URL é entrada do usuário, e `?atividade=foo`
 * tem de virar ausência em vez de uma aba que a tela não sabe desenhar.
 *
 * O seletor mora na URL pelo mesmo motivo que um filtro — quem manda o link de
 * "Laudos" tem de abrir Laudos —, **mas ele não é um filtro**: ele troca o
 * quadro. É a armadilha que ela mesma apontou na gravação (27:16), *"não é
 * filtro, porque a coluna não muda"*.
 */
export const SELETOR_DA_ELABORACAO = [
  "todos",
  "quesitos",
  "laudos",
  "impugnacao",
] as const;

export type SelecaoDaElaboracao = (typeof SELETOR_DA_ELABORACAO)[number];

export function isSelecaoDaElaboracao(
  valor: string,
): valor is SelecaoDaElaboracao {
  return (SELETOR_DA_ELABORACAO as readonly string[]).includes(valor);
}

function mesmoDia(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Vence hoje — comparado no fuso de quem olha, que é onde o prazo importa. */
export function venceHoje(item: ItemDaEtapa, agora: Date): boolean {
  const prazo = dataDoPrazo(item);
  return prazo ? mesmoDia(prazo, agora) : false;
}

/** Vence nos próximos sete dias, contando de hoje e sem incluir o vencido. */
export function venceNaSemana(item: ItemDaEtapa, agora: Date): boolean {
  const prazo = dataDoPrazo(item);
  if (!prazo) return false;
  // Mesma fronteira do vencido (TUT-321): o de hoje, mesmo com a hora passada,
  // ainda está na semana.
  if (estaVencido(item, agora)) return false;
  const limite = new Date(agora.getTime());
  limite.setDate(limite.getDate() + 7);
  return prazo.getTime() <= limite.getTime();
}

/**
 * Agendamento — as SEIS pendências do rascunho dela (10/09), na ordem em que
 * ela as desenhou: PERÍCIA · AGENDAMENTO · ASSISTENTE · RELATÓRIO · ENVIO ·
 * PROTOCOLO. A fala (12:15) tinha listado três; o desenho, feito com mais
 * calma, tem seis — e escrito ganha de falado.
 *
 * O rótulo é o DELA: "Aguardando relatório", não "pendente de laudo". No
 * vocabulário do escritório, relatório/parecer técnico é o que a equipe
 * escreve; laudo é o do perito. Trocar uma palavra pela outra é o tipo de
 * "vocabulário alheio" de que ela reclamou (07:29).
 *
 * **As SEIS têm estado no domínio hoje (TUT-243).** O comentário anterior
 * desta constante dizia que assistente/envio/protocolo "ainda não têm estado"
 * — estava desatualizado: faltava o CONTADOR, não o estado.
 * - "Pendente assistente" = perícia AGENDADA sem assistente técnico
 *   (TUT-309, decisão do dono em 25/09; até ali era a peça
 *   `ASSISTANT_NOMINATION` em aberto). A borda da tela entrega esse item com
 *   o status sintético `sem_assistente`, e "Aguardando perícia" passa a ser a
 *   agendada COM assistente;
 * - "Pendente envio" = peça (qualquer tipo) em `AWAITING_DELIVERY` — o mesmo
 *   status que a Elaboração já lê em `Finalizados`; o Agendamento continua o
 *   MESMO fluxo dali em diante (decisão 17 da fase 2);
 * - "Pendente envio" também é o laudo (`expert_reports`) em `AWAITING_FILING`
 *   ainda sem envio ao advogado (TUT-530, revisão); "Aguardando protocolo" é só
 *   o envio que já foi e espera o comprovante.
 *
 * Cada fonte é uma consulta que já existe (`api.expertises`,
 * `api.legalBriefsAcrossProcesses`, `api.expertReportsAcrossProcesses`) — a
 * tela (`AgendamentoPage.tsx`) soma as seis num resumo único; este módulo
 * declara só o RÓTULO, para o resumo e o quadro (`etapaAgendamento`, abaixo)
 * nunca discordarem um do outro.
 *
 * **TUT-244 — o campo `filtro` saiu daqui, e o motivo é uma re-decisão.**
 * A TUT-231 leu "quando você clica nos vencidos, me abre a lista" (32:17)
 * como NAVEGAÇÃO, e por isso a pendência declarava aqui a rota que abria. O
 * quadro que a cliente mandou em 10/09 responde outra coisa: os quatro
 * cartões que ela comentou dizem "POP UP", e a lista abre POR CIMA da tela,
 * sem trocar de página. O que a pendência abre passou a viver em
 * `lib/foco.ts`, que declara o mesmo recorte e mais o que a navegação não
 * precisava — as colunas da lista e qual foco cada linha abre. Manter
 * `filtro` ao lado dele seria guardar dois de-paras para a mesma pergunta, e
 * no dia em que discordassem o contador e a lista discordariam junto.
 *
 * O efeito colateral que fecha o argumento: "Pendente protocolo" era o único
 * cartão SEM destino, porque não existe lista de laudos endereçável por URL.
 * Com o pop-up ele deixou de ser exceção — a lista não precisa mais ser uma
 * tela.
 */
export interface PendenciaDoAgendamento {
  chave: "pericia" | "agendamento" | "assistente" | "relatorio" | "envio" | "protocolo";
  titulo: string;
  /**
   * TUT-327 — a EXCEÇÃO declarada à regra da TUT-313 ("o contador filtra o
   * quadro"). Decisão do dono (27/09, pergunta 3a): "Pendente assistente" volta
   * a abrir a lista direto, e dela a ficha com "Indicar assistente" — resolver
   * a pendência é o gesto, e o clique a mais no "Ver lista" atrapalhava. Os
   * outros cinco continuam filtrando. Exceção nova entra AQUI, com o motivo.
   */
  abreALista?: true;
}

/**
 * TUT-530 (revisão, decisão do dono de 25/10: "são 7", "precisa estar igual o
 * Canva") — os SEIS contadores do topo, na ordem do desenho de 23/10:
 * Aguardando perícia · Pendente agendamento · Pendente assistente · Aguardando
 * relatório · Pendente envio · Aguardando protocolo.
 *
 * - "Pendente formulário" (TUT-526, M12) saiu do TOPO — o Canva não o tem —, mas
 *   continua COLUNA do quadro, com "Ver lista" (`FOCOS_DE_COLUNA_SEM_CONTADOR`).
 * - "Pendente protocolo" virou "Aguardando protocolo": a chave `protocolo` e o
 *   foco `pendente-protocolo` ficam (links salvos), o título é o do Canva, e ele
 *   conta SÓ o que já foi enviado e espera o comprovante do advogado. O laudo
 *   ainda sem envio é "Pendente envio" (ver `contaNoContador`).
 * - "Aguardando perícia" e "Aguardando protocolo" não são mais colunas
 *   (`contaNoContador` diz o que cada um conta).
 *
 * TUT-526 — a perícia agendada COM assistente cujo roteiro
 * (`expertise-intakes`) não passou da preparação é "Pendente formulário", e
 * não "Aguardando perícia": uma perícia, uma coluna.
 *
 * E "Pendente agendamento" deixou de ser só a perícia "a agendar": o aceite
 * não cria mais a peça nem o laudo (D12), e a solicitação aceita sem a ficha
 * de agendamento — e o laudo nascido do relatório (D39) — esperam ali, com o
 * status sintético `pendente_agendamento` que a borda da tela dá a eles.
 */
export const pendenciasDoAgendamento: readonly PendenciaDoAgendamento[] = [
  { chave: "pericia", titulo: "Aguardando perícia", abreALista: true },
  { chave: "agendamento", titulo: "Pendente agendamento" },
  { chave: "assistente", titulo: "Pendente assistente", abreALista: true },
  { chave: "relatorio", titulo: "Aguardando relatório" },
  { chave: "envio", titulo: "Pendente envio" },
  { chave: "protocolo", titulo: "Aguardando protocolo", abreALista: true },
];

/**
 * TUT-530 (revisão) — a lista que o "Ver lista" de uma coluna abre quando a
 * coluna NÃO tem contador no topo: só "Pendente formulário". A guarda de
 * `foco.test.ts` ("todo pop-up tem contador") soma isto aos contadores, de modo
 * que a exceção é declarada AQUI, e foco órfão continua reprovando.
 */
export const FOCOS_DE_COLUNA_SEM_CONTADOR: readonly ChaveDeFoco[] = ["pendente-formulario"];

/**
 * TUT-530 (revisão) — o que os dois contadores sem coluna contam, por item.
 *
 * "Aguardando perícia" e "Aguardando protocolo" saíram do Kanban, mas o número
 * do topo continua sendo contado sobre os MESMOS itens da tela (com o recorte
 * de cliente e "Atribuídos a mim"): quem diz o que cada um conta é este
 * predicado, e a lista dele (`FOCOS`) tem de contar igual.
 *
 * - perícia: a agendada com assistente e roteiro pronto, ou a que aguarda a data
 *   do juízo (TUT-527) — a que vai acontecer sem o escritório agir agora;
 * - protocolo: SÓ o envio ao advogado que já foi e espera o comprovante
 *   (TUT-530, D19). O laudo em `AWAITING_FILING` ainda sem envio é trabalho da
 *   M&H por fazer: "Pendente envio", ao lado da peça (decisão do controlador,
 *   25/10: o Canva separa "a M&H age" de "esperar o advogado").
 */
export const contaNoContador: Record<"pericia" | "protocolo", (item: ItemDaEtapa) => boolean> = {
  pericia: (item) => item.status === "scheduled" || item.status === "awaiting_court_date",
  protocolo: (item) => item.status === "envio_aguardando_protocolo",
};

/** TUT-530 (revisão) — o status que a borda dá à solicitação do Portal que espera o aceite. */
export const STATUS_SOLICITADA = "solicitada";

/**
 * TUT-530 (revisão) — `?pendencia=` de antes do quadro de sete colunas.
 *
 * "pericia" e "protocolo" filtravam o quadro para a coluna deles; as colunas
 * saíram. "assistente" e "formulario" ainda são coluna, mas sem contador que as
 * filtre e desfaça. Um link salvo não pode virar quadro vazio, nem preso numa
 * coluna sem saída: a rota o traduz para a LISTA do mesmo assunto (o foco que o
 * clique abre hoje).
 */
export function focoDaPendenciaSemColuna(chave: unknown): ChaveDeFoco | undefined {
  if (chave === "pericia") return "pendente-pericia";
  if (chave === "protocolo") return "pendente-protocolo";
  // Sem contador que filtre (o assistente abre a lista, TUT-327; o formulário não
  // tem contador): o quadro de uma coluna só não teria marca nem botão para voltar.
  if (chave === "assistente") return "pendente-assistente";
  if (chave === "formulario") return "pendente-formulario";
  return undefined;
}

/**
 * TUT-530 (revisão) — as chaves de `?pendencia=` que ainda FILTRAM o quadro: só as
 * dos contadores que o filtram (marcados, e desfeitos ao clicar de novo).
 */
const PENDENCIAS_QUE_FILTRAM = ["agendamento", "relatorio", "envio"] as const;
export type PendenciaQueFiltra = (typeof PENDENCIAS_QUE_FILTRAM)[number];

export function ehPendenciaQueFiltra(chave: unknown): chave is PendenciaQueFiltra {
  return typeof chave === "string" && (PENDENCIAS_QUE_FILTRAM as readonly string[]).includes(chave);
}

/**
 * O quadro do Agendamento (TUT-243) — a pergunta 2.2 da fase 2 foi decidida
 * em 14/09: as pendências viram as colunas, NA ORDEM DO FLUXO.
 *
 * TUT-530 (revisão, decisão do dono de 25/10: "são 7", "igual o Canva") — o
 * quadro tem SETE colunas, como o Kanban do Canva de 23/10: Solicitações
 * realizadas · Pendente agendamento · Pendente assistente · Pendente formulário
 * · Aguardando relatório · Aguardando cliente · Pendente envio. Com nove, o
 * quadro quebrava em duas linhas no desktop (`tut-332-quadros-numa-linha`);
 * sete têm o caso próprio em `colunasPorLinha`.
 *
 * "Aguardando perícia" e "Aguardando protocolo" saíram do quadro e ficaram
 * como contador e lista (ver `contaNoContador`): o item que nenhuma coluna
 * aceita fica de fora do quadro, de propósito (`distribuir`).
 *
 * O status de cada coluna é o vocabulário NATIVO da fonte que a alimenta —
 * `ItemDaEtapa.status` chega já normalizado pela borda da tela
 * (`AgendamentoPage.tsx`), como a Elaboração já faz para o laudo
 * (`paraItemDeLaudo`): perícia fala `to_schedule`/`scheduled`/
 * `pending_report`; peça fala `drafting`/`in_review`/`awaiting_delivery`;
 * laudo fala `awaiting_filing`; a solicitação do Portal fala `solicitada`. As
 * fontes só se cruzam neste quadro porque a tela busca de cada uma exatamente
 * o recorte que a coluna espera — duas colunas nunca competem pelo mesmo item.
 */
export const etapaAgendamento: DefinicaoDeEtapa = {
  chave: "agendamento",
  titulo: "Agendamento",
  subtitulo:
    "As pendências do agendamento, da solicitação que chegou ao envio.",
  colunas: [
    {
      // TUT-530 (revisão) — as solicitações do Portal do advogado que chegaram
      // e esperam o aceite (PENDING, "Solicitado" no Canva). O cartão abre a
      // ficha da solicitação, com Aceitar e Recusar. O "Ver lista" dela abre o
      // painel de solicitações (`AgendamentoPage`), e não um foco.
      chave: "solicitacoes",
      titulo: "Solicitações realizadas",
      aceita: (item) => item.status === STATUS_SOLICITADA,
    },
    {
      chave: "agendamento",
      titulo: "Pendente agendamento",
      // TUT-526 — a perícia "a agendar" e o que espera a ficha de agendamento
      // (a solicitação aceita e o laudo nascido do relatório).
      aceita: (item) => item.status === "to_schedule" || item.status === "pendente_agendamento",
    },
    {
      chave: "assistente",
      titulo: "Pendente assistente",
      aceita: (item) => item.status === "sem_assistente",
    },
    {
      chave: "formulario",
      titulo: "Pendente formulário",
      aceita: (item) => item.status === "formulario_pendente",
    },
    {
      chave: "relatorio",
      titulo: "Aguardando relatório",
      aceita: (item) => item.status === "pending_report",
    },
    // TUT-530 (D4, P4) — o "Outros" enviado ao advogado e sem resposta. Não é
    // pendência do topo, nem tem lista: o estado do item não muda por arraste.
    // (O protocolo enviado que espera o comprovante conta em "Aguardando
    // protocolo", no topo.)
    {
      chave: "aguardando-cliente",
      titulo: "Aguardando cliente",
      aceita: (item) => item.status === "envio_aguardando_cliente",
      semLista: true,
    },
    {
      chave: "envio",
      titulo: "Pendente envio",
      // TUT-530 (revisão) — a peça e o laudo (`awaiting_filing`) que a M&H ainda
      // não enviou ao advogado: o cartão leva "Enviar ao advogado".
      aceita: (item) => item.status === "awaiting_delivery" || item.status === "awaiting_filing",
    },
  ],
};

export const etapas: Record<ChaveDeEtapa, DefinicaoDeEtapa> = {
  elaboracao: etapaElaboracao,
  agendamento: etapaAgendamento,
};
